/**
 * Fallback / placeholder data shown when:
 *  1. An endpoint is unavailable (404, network error)
 *  2. The response is empty (no records yet)
 *
 * These are clearly illustrative — never blocking render.
 */

import type { Priority, AttentionProject, ScoredDeal, Approval } from "./types.js";

/** Shown when no priorities can be fetched — demonstrates the expected shape */
export const FALLBACK_PRIORITIES: Priority[] = [
  {
    id: "p-fallback-1",
    title: "Review permit response from Building Dept.",
    project: "Elm St. Townhomes",
    level: "urgent",
    dueDate: "Today",
    type: "Permit",
  },
  {
    id: "p-fallback-2",
    title: "Approve structural inspection quote",
    project: "Oak Ave. ADU",
    level: "high",
    dueDate: "Tomorrow",
    type: "Procurement",
  },
  {
    id: "p-fallback-3",
    title: "Follow up with broker on 4th St. listing",
    level: "high",
    dueDate: "This week",
    type: "Acquisition",
  },
  {
    id: "p-fallback-4",
    title: "Update budget forecast for Q3",
    project: "Pine Blvd. Renovation",
    level: "medium",
    type: "Finance",
  },
  {
    id: "p-fallback-5",
    title: "Send weekly progress report to Eli",
    project: "Portfolio",
    level: "low",
    type: "Reporting",
  },
];

/** Shown when no projects need attention — illustrative only */
export const FALLBACK_ATTENTION_PROJECTS: AttentionProject[] = [
  {
    id: "proj-fallback-1",
    name: "Elm St. Townhomes",
    status: "red",
    reason: "Permit delayed 3 weeks",
    phase: "Entitlement",
  },
  {
    id: "proj-fallback-2",
    name: "Oak Ave. ADU",
    status: "yellow",
    reason: "Budget variance +12%",
    phase: "Construction",
  },
  {
    id: "proj-fallback-3",
    name: "Pine Blvd. Renovation",
    status: "yellow",
    reason: "Awaiting contractor scope",
    phase: "Pre-construction",
  },
];

/** Shown when no scored deals are available */
export const FALLBACK_SCORED_DEALS: ScoredDeal[] = [
  {
    id: "deal-fallback-1",
    address: "127 Maple Street",
    score: 87,
    scoreTier: "high",
    askPrice: "$1.4M",
    capRate: "6.2%",
    addedDate: "Yesterday",
  },
  {
    id: "deal-fallback-2",
    address: "402 Pacific Ave, Unit B",
    score: 74,
    scoreTier: "high",
    askPrice: "$890K",
    capRate: "5.8%",
    addedDate: "2 days ago",
  },
  {
    id: "deal-fallback-3",
    address: "18 Commerce Dr",
    score: 61,
    scoreTier: "medium",
    askPrice: "$2.1M",
    capRate: "4.9%",
    addedDate: "This week",
  },
];

/** Shown when no approvals are pending */
export const FALLBACK_APPROVALS: Approval[] = [
  {
    id: "appr-fallback-1",
    title: "Concrete subcontractor quote — $48,200",
    project: "Elm St. Townhomes",
    type: "Quote",
    requestedBy: "Development Agent",
    requestedAt: "2h ago",
  },
  {
    id: "appr-fallback-2",
    title: "Change order #7 — electrical panel upgrade",
    project: "Oak Ave. ADU",
    type: "Change Order",
    requestedBy: "Site Manager",
    requestedAt: "Yesterday",
  },
];
